<template>
  <div>
    <div class="simulation-alert" v-if="alertMessage">
      <i class="fas fa-exclamation-circle"></i>
      <span v-html="alertMessage"></span>
    </div>
    <div class="form-group small-margin">
      <div class="simulation-group-row1" :class="classNames">
        <div class="simulation-group-text bridal-item-name">
          <i v-if="serviceAmountTarget" class="icon-star fas fa-star"></i>
          <a href="#" @click.prevent="showItemModal()" v-html="bridalItemName">
          </a>
        </div>
        <div class="simulation-group-text tax-exempt-name">
          {{taxExempt}}
        </div>
      </div>
      <div class="simulation-group-row2" :class="classNames">
        <div class="simulation-group-input retail-price">
          <input
            type="text"
            inputmode="numeric"
            class="form-control small smallheight digits-8 text-align-right"
            :class="{ 'readonly': !isRetailPriceEditable }"
            maxlength="10"
            :value="dataPrice"
            :readonly="!isRetailPriceEditable || isDeleted"
            @focus="focusRetailPrice"
            @blur="blurRetailPrice"
          />
        </div>
        <div class="simulation-group-text multiplication">
          ×
        </div>
        <div class="simulation-group-input quantity">
          <input
            type="text"
            inputmode="numeric"
            class="form-control small smallheight digits-3 text-align-right"
            :class="{ 'readonly': !isQuantityEditable }"
            maxlength="9"
            :value="dataQuantity"
            :readonly="!isQuantityEditable || isDeleted"
            @focus="focusQuantity"
            @blur="blurQuantity"
          />
        </div>
        <div class="simulation-group-text retail-amount">
          &yen;{{retailAmount|priceFormat}}
        </div>
        <div class="list-group-control delete">
          <a
            @click="onDelete"
            v-if="isDeletable"
          >
            <i class="icon-trash fas fa-ban" />
          </a>
          <a
            @click="onUndo"
            v-if="deleted"
          >
            <i class="icon-undo fas fa-undo-alt" />
          </a>
        </div>
        <div v-if="isPlanner && this.editable" class="simulation-group-check">
          <label class="checkbox">
            <input
              :id="`select-fix-${simulationBridalItemId}`"
              type="checkbox"
              :checked="isFixed"
              :disabled="deleted"
              @change="onCheck"
            />
            <span class="checkbox-text"></span>
          </label>
        </div>
        <div v-if="isCustomer || !this.editable" class="simulation-group-check">
          <i v-if="isFixed" class="icon-fixed fas fa-check" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import priceFormat from '@/filters/price-format'
import {getCommaPrice, getCommaRemovedPrice} from '@/utils/form'

const MAX_ABS_PRICE = 999999999
const MAX_ABS_QUANTITY = 999999999

export default {
  props: {
    simulationBridalItemId: {
      type: Number,
      required: true
    },
    bridalItemGroupId: {
      type: Number,
      required: true
    },
    bridalItemGroupName: {
      type: String,
      required: true
    },
    bridalItemGroupExclusionFlag: {
      type: Boolean,
      required: true
    },
    bridalItemSubgroupId: {
      type: Number,
      required: true
    },
    bridalItemSubgroupName: {
      type: String,
      required: true
    },
    bridalItemName: {
      type: String,
      required: true
    },
    bridalItemDescription: {
      type: String,
      required: false
    },
    serviceAmountTarget: {
      type: Boolean,
      required: true
    },
    taxExempt: {
      type: String,
      required: true
    },
    masterRetailPrice: {
      type: String,
      required: true
    },
    retailPriceEditableFlag: {
      type: Boolean,
      required: true
    },
    quantityEditableFlag: {
      type: Boolean,
      required: true
    },
    itemNumber: {
      type: String,
      required: false,
      default: ''
    },
    alertMessage: {
      type: String,
      required: false
    },
    bridalItemId: {
      type: Number,
      required: true
    },
    imagePath: {
      type: String,
      required: false
    },
    retailPrice: {
      type: String,
      required: true
    },
    quantity: {
      type: Number,
      required: true
    },
    fixFlag: {
      type: Boolean,
      required: true
    },
    retailAmount: {
      type: Number,
      required: true
    },
    revision: {
      type: Number,
      required: true
    },
    deleted: {
      type: Boolean,
      required: false,
      default: false
    },
    editable: {
      type: Boolean,
      required: true,
      default: false
    },
  },

  filters: {
    priceFormat,
  },

  data() {
    return {
      dataPrice: this.retailPrice,
      dataQuantity: this.quantity,
      isDeleted: this.deleted,
    }
  },

  computed: {
    classNames() {
      return {
        'is-fixed': this.fixFlag,
        'is-deleted': this.isDeleted,
      }
    },
    isPlanner() {
      return this.$store.getters['auth/isPlanner']
    },
    isCustomer() {
      return this.$store.getters['auth/isCustomer']
    },
    isFixed() {
      return this.fixFlag
    },
    isRetailPriceEditable() {
      return this.editable && this.retailPriceEditableFlag && !this.fixFlag
    },
    isQuantityEditable() {
      return this.editable && this.quantityEditableFlag && !this.fixFlag
    },
    isDeletable() {
      return this.editable && !this.deleted && !this.fixFlag
      && !this.bridalItemGroupExclusionFlag
    }
  },

  methods: {
    // 削除する
    onDelete() {
      this.isDeleted = true
      this.$emit('delete', this.simulationBridalItemId)
    },

    // 削除を戻す
    onUndo() {
      this.isDeleted = false
      this.$emit('undo', this.simulationBridalItemId)
    },

    // 確定チェックをクリックする
    onCheck() {
      this.$emit('check', this.simulationBridalItemId)
    },

    // 上代にフォーカスをあてる
    focusRetailPrice($event) {
      if (this.retailPriceEditableFlag && !this.fixFlag) {
        // 編集できるときだけ上代カンマ除去
        this.dataPrice = getCommaRemovedPrice($event.target.value, MAX_ABS_PRICE)
        this.$emit('changeRetailPrice', this.simulationBridalItemId, this.dataPrice)
      }
    },

    // 上代からフォーカスを外す
    blurRetailPrice($event) {
      if (this.retailPriceEditableFlag && !this.fixFlag) {
        // 編集できるときだけ上代カンマ付与
        this.dataPrice = getCommaPrice($event.target.value, MAX_ABS_PRICE)
        this.$emit('changeRetailPrice', this.simulationBridalItemId, this.dataPrice)
      }
    },

    // 数量にフォーカスをあてる
    focusQuantity($event) {
      if (this.quantityEditableFlag && !this.fixFlag) {
        // 編集できるときだけ数量カンマ除去
        this.dataQuantity = getCommaRemovedPrice($event.target.value, MAX_ABS_QUANTITY)
        this.$emit('changeQuantity', this.simulationBridalItemId, this.dataQuantity)
      }
    },

    // 数量からフォーカスを外す
    blurQuantity($event) {
      if (this.quantityEditableFlag && !this.fixFlag) {
        // 編集できるときだけ数量カンマ付与
        this.dataQuantity = getCommaPrice($event.target.value, MAX_ABS_QUANTITY, false)
        this.$emit('changeQuantity', this.simulationBridalItemId, this.dataQuantity)
      }
    },

    // 婚礼アイテムモーダルを表示する
    showItemModal() {
      this.$emit('showItemModal', this.bridalItemId)
    },
  },

  // 履歴からシミュレーションに遷移した時に
  // 金額や数量が変わっていると画面に反映されないので
  // 値の変化を監視
  watch: {
    // 上代単価
    retailPrice: {
      handler() {
        this.dataPrice = this.retailPrice
      }
    },
    // 数量
    quantity: {
      handler() {
        this.dataQuantity = this.quantity
      }
    },
  },
}
</script>
